import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useReconnectingEventSource } from './useReconnectingEventSource';
import { useAuth } from './useAuth';
import { API_URL } from '../lib/api';

/**
 * Subscribes to account-level SSE events for real-time updates.
 * Writes balance-updated events directly into the react-query cache
 * so useCreditBalance picks them up instantly.
 *
 * Only connects when authenticated — prevents 401 reconnection floods
 * on unauthenticated pages (login, terms, etc.).
 */
export function useAccountEvents() {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuth();

  // Only connect when authenticated; null URL disables the hook
  const url = !isLoading && isAuthenticated
    ? `${API_URL}/api/v2/cloud/events`
    : null;

  const setup = useCallback(
    (es: EventSource) => {
      es.addEventListener('balance-updated', (event) => {
        try {
          const data = JSON.parse(event.data);
          queryClient.setQueryData(
            ['creditBalance'],
            (old: Record<string, unknown> | undefined) => ({
              ...old,
              balanceCredits: data.totalSpendableCredits,
              totalSpendableCredits: data.totalSpendableCredits,
              monthlyRemainingCredits: data.monthlyRemainingCredits,
              topUpBalanceCredits: data.topUpBalanceCredits,
              periodUsageCredits: data.periodUsageCredits,
              periodAllowanceCredits: data.periodAllowanceCredits,
              lifetimeCredits: data.lifetimeCredits,
              updatedAt: data.updatedAt,
            }),
          );
        } catch (error) {
          console.warn(
            '[AccountEvents] Failed to process balance-updated event:',
            error,
          );
        }
      });

      es.addEventListener('plan-updated', () => {
        // Invalidate both subscription and balance queries to refetch plan state
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
      });

      es.addEventListener('duplicate-progress', (event) => {
        try {
          const data = JSON.parse(event.data);
          queryClient.setQueryData(
            ['duplicateProgress'],
            (old: Record<string, number> | undefined) => ({
              ...old,
              [data.siteId]: data.percent,
            }),
          );
        } catch (error) {
          console.warn(
            '[AccountEvents] Failed to process duplicate-progress event:',
            error,
          );
        }
      });
    },
    [queryClient],
  );

  useReconnectingEventSource(url, setup);
}
